import { Icon28ChevronUpOutline } from '@vkontakte/icons';
import { Button, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import balloonImageLeft from 'img/freemium/onboardingBallonLeft.png';
import balloonImageRight from 'img/freemium/onboardingBallonRight.png';
import balloonImage from 'img/freemium/onboardingBalloon.png';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    freemiumOnboardBannerIsInitialOpen,
    freemiumTrialTariffId,
    freemiumTypeParam,
    FreemiumTypes,
    isControlFreemium,
    isHardFreemium,
    isSoftFreemium,
} from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { BillingActions } from 'reactApp/modules/billing/billing.module';
import { BillingSelectors } from 'reactApp/modules/billing/billing.selectors';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { openFreemiumFeaturesModal } from 'reactApp/sections/FamilyPage/FreemiumFeaturesModal/FreemiumFeaturesModal.helpers';
import type { FreemiumFeaturesModalData } from 'reactApp/sections/FamilyPage/FreemiumFeaturesModal/FreemiumFeaturesModal.types';
import { sendDwh, sendGa, sendXray } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';

import styles from './FreemiumOnboardBanner.css';
import { defaultBalloonProps, defaultModalProps } from './FreemiumOnboardBanner.helpers';
import type { FreemiumOnboardModalProps } from './FreemiumOnboardBanner.types';

export const FreemiumOnboardBannerWrapper = () => {
    const props: FreemiumOnboardModalProps = freemiumTypeParam
        ? defaultBalloonProps[freemiumTypeParam]
        : defaultBalloonProps[FreemiumTypes.control];

    return <FreemiumOnboardBanner {...props} />;
};

export const FreemiumOnboardBanner = ({ title, text, topImage, buttonText, imageClassName }: FreemiumOnboardModalProps) => {
    const dispatch = useDispatch();
    const isLoaded = useSelector(BillingSelectors.getFreemiumIsLoaded);
    const [open, setOpen] = useState(freemiumOnboardBannerIsInitialOpen);
    const modalProps: FreemiumFeaturesModalData = freemiumTypeParam
        ? defaultModalProps[freemiumTypeParam]
        : defaultModalProps[FreemiumTypes.control];

    const dwhName = isControlFreemium
        ? 'mnz_family_unlimited_trial'
        : isHardFreemium
        ? 'mnz_family_unlimited_hard_freemium'
        : 'mnz_family_unlimited_soft_freemium';

    const modalOpen = useCallback(() => {
        if (isSoftFreemium) {
            dispatch(BillingActions.getFreemium());
        } else if (isControlFreemium) {
            dispatch(
                openTariffBuy({
                    productId: freemiumTrialTariffId,
                    onSuccess: () => {
                        sendGa('success');
                        sendXray(['freemium', 'trial', 'buy', 'success']);
                        opener('/family/folders', true);
                    },
                    paySource: 'family_unlimited_trial',
                })
            );
        } else if (isHardFreemium) {
            opener('/family/folders', true);
        }
    }, [dispatch, opener, isLoaded]);

    const onOpenClick = () => {
        const action = open ? 'click-hide' : 'click-open';
        sendDwh({
            eventCategory: 'banner',
            action,
            dwhData: { name: dwhName },
            dwh_enabled: true,
        });
        setOpen(!open);
    };

    const onButtonClick = () => {
        if (isHardFreemium) {
            dispatch(BillingActions.getFreemium());
        }
        sendDwh({ eventCategory: 'banner', action: 'click', dwhData: { name: dwhName }, dwh_enabled: true });
        openFreemiumFeaturesModal({ ...modalProps, onButtonClick: modalOpen });
    };
    return (
        <div className={classNames(styles.root, { [styles.rootBigger]: open, [styles.rootBiggerWithText]: open && !!text })}>
            <div className={classNames(styles.iconWrapper, { [styles.iconWrapperRotate]: open })} onClick={onOpenClick}>
                <Icon28ChevronUpOutline width={20} height={20} />
            </div>
            <div className={classNames(styles.smallState, { [styles.isNotVisible]: open })}>
                <img src={balloonImage} alt="freemium" className={styles.image} />
                <Text className={styles.title} weight="2">
                    Дарим безлимит для&nbsp;фото с телефона
                </Text>
            </div>
            <div className={classNames(styles.bigState, { [styles.isNotVisible]: !open })}>
                <img src={balloonImageLeft} alt="freemium" className={styles.imageLeft} />
                <img src={balloonImageRight} alt="freemium" className={styles.imageRight} />
                <img src={topImage} alt="freemium" className={classNames(styles.image, imageClassName)} />
                <div className={styles.bottomBlock}>
                    <Text className={styles.title} weight="2">
                        {title}
                    </Text>
                    {!!text && (
                        <>
                            <Spacing size={8} />
                            <Text className={styles.text}>{text}</Text>
                        </>
                    )}
                    <Spacing size={18} />
                    <Button size="m" appearance="neutral" mode="primary" className={styles.button} onClick={onButtonClick}>
                        {buttonText}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FreemiumOnboardBannerWrapper;
